import { db } from 'codemash';
import i18next from 'i18next';
import { Config } from '../configs';
import { mapApiToDataTableOrders } from './mappers/order';
import { restaurantTypes } from '../enums/restaurantTypes';
import { currentUtcMillis } from '../utils/dateHelpers';
import { manageApiUnavailableError } from './error/errors';
import { apiActionChooser } from '../utils/apiActionChooser';
import { resourceRequest } from './client/requests';

export const getOrderByTable = async ({ tableId, userId, restaurantType }) => {
  try {
    let response;

    if (restaurantType === restaurantTypes.OrderAndPay) {
      const showFromMillis = currentUtcMillis() - 24 * 3600000;

      await apiActionChooser(
        global.localStorage.getItem('v2-company-id'),
        async () => {
          response = await db.executeAggregate({
            id: Config.DATA.AGGREGATES.GET_TABLE_USER_ORDERS,
            collectionName: Config.DATA.COLLECTIONS.ORDERS,
            database: Config.CODEMASH.DB_STORAGE_ID,
            tokens: {
              tableId,
              userId,
              fromMillis: showFromMillis,
            },
          });
        },
        async () => {
          response = await resourceRequest({
            data: {
              id: 'ClientGetTableUserOrders',
              tokens: {
                tableId,
                fromMillis: showFromMillis,
              },
            },
            action: 'aggregate',
          });
        },
      );
    } else {
      await apiActionChooser(
        global.localStorage.getItem('v2-company-id'),
        async () => {
          response = await db.executeAggregate({
            id: Config.DATA.AGGREGATES.GET_TABLE_ORDER,
            collectionName: Config.DATA.COLLECTIONS.ORDERS,
            database: Config.CODEMASH.DB_STORAGE_ID,
            tokens: {
              tableId,
              userId,
            },
          });
        },
        async () => {
          response = await resourceRequest({
            data: {
              id: 'ClientGetTableActiveOrder',
              tokens: {
                tableId,
              },
            },
            action: 'aggregate',
          });
        },
      );
    }

    if (response) {
      return mapApiToDataTableOrders({ orders: response });
    }
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to get table orders', e);
    }
  }

  return null;
};

export const rateOrder = async ({
  token,
  restaurantId,
  orderId,
  paymentId,
  userId,
  rating,
  comment,
}) => {
  try {
    if (paymentId) {
      await apiActionChooser(
        global.localStorage.getItem('v2-company-id'),
        async () => {
          await db.replaceRecordWithFilter({
            secretKey: token,
            collectionName: Config.DATA.COLLECTIONS.RATINGS,
            database: Config.CODEMASH.DB_STORAGE_ID,
            filter: {
              payment: { $oid: paymentId },
              client_user: userId,
            },
            document: {
              restaurant: restaurantId,
              order: orderId,
              client_user: userId,
              payment: paymentId,
              rating,
              comment,
            },
            isUpsert: true,
          });
        },
        async () => {
          await resourceRequest({
            data: {
              resource: 'ClientReviews',
              document: JSON.stringify({
                restaurant: restaurantId,
                order: orderId,
                payment: paymentId,
                rating,
                comment,
              }),
              filter: JSON.stringify({
                payment: { $oid: paymentId },
              }),
              isUpsert: true,
            },
            action: 'replaceOne',
          });
        },
      );
    } else if (orderId) {
      await apiActionChooser(
        global.localStorage.getItem('v2-company-id'),
        async () => {
          await db.replaceRecordWithFilter({
            secretKey: token,
            collectionName: Config.DATA.COLLECTIONS.RATINGS,
            database: Config.CODEMASH.DB_STORAGE_ID,
            filter: {
              order: { $oid: orderId },
              client_user: userId,
            },
            document: {
              restaurant: restaurantId,
              order: orderId,
              client_user: userId,
              payment: paymentId,
              rating,
              comment,
            },
            isUpsert: true,
          });
        },
        async () => {
          await resourceRequest({
            data: {
              resource: 'ClientReviews',
              document: JSON.stringify({
                restaurant: restaurantId,
                order: orderId,
                payment: paymentId,
                rating,
                comment,
              }),
              filter: JSON.stringify({
                order: { $oid: orderId },
              }),
              isUpsert: true,
            },
            action: 'replaceOne',
          });
        },
      );
    }
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to update rating', e);
    }
  }
};

export const rateRestaurant = async ({
  token,
  restaurantId,
  userId,
  rating,
  comment,
  alertController,
}) => {
  try {
    await apiActionChooser(
      global.localStorage.getItem('v2-company-id'),
      async () => {
        await db.insertRecord({
          secretKey: token,
          collectionName: Config.DATA.COLLECTIONS.RATINGS,
          database: Config.CODEMASH.DB_STORAGE_ID,
          document: {
            restaurant: restaurantId,
            client_user: userId,
            rating,
            comment,
          },
        });
      },
      async () => {
        await resourceRequest({
          data: {
            resource: 'ClientReviews',
            document: JSON.stringify({
              restaurant: restaurantId,
              rating,
              comment,
            }),
          },
          action: 'insertOne',
        });
      },
    );

    if (alertController) {
      alertController.success({ text: i18next.t('payment.reviewCreated') });
    }
  } catch (e) {
    manageApiUnavailableError(e);

    if (Config.SHOW_LOGS) {
      console.log('Error while trying to insert rating', e);
    }
  }
};
